import { Component, createRef } from 'preact';
import * as THREE from 'three';
import { FOIL_VERTEX_SHADER, FOIL_FRAGMENT_SHADER } from '../utils/sorcery/foilShader.js';
import { FOIL_WHITE_ZONES } from '../utils/sorcery/foilZones.js';
import { DEFAULT_FOIL_PARAMS, paramUniformName } from '../utils/sorcery/foilParams.js';
import { buildFoilUniforms, foilPlaceholderTexture, getFoilMatcap, loadFoilTextures } from '../utils/sorcery/foilMaterial.js';
import { isPhoneLayout } from '../utils/platform.js';

// ONE persistent WebGL context shared by every transient single-card foil
// preview (deck-builder sidebar hover + [card:id] mention hover). Only one
// preview is ever visible, so a single reused context renders them all: we
// never create or destroy a GL context on hover. This is what keeps rapid
// hovering from piling up contexts toward the browser's ~16-context ceiling.
// which, once hit, makes the browser silently kill the OLDEST live context
// (the grid's CardFoilScene) and spew "delete: object does not belong to this
// context" as Three deletes buffers/VAOs against the recycled context, then
// white-crashes the grid. The lone canvas is reparented into whichever preview
// wrapper is active and removed (context kept alive) when idle.
//
// Swapping the card texture inside this single context is safe: the texture is
// always created and disposed by the SAME renderer, so it can never outlive its
// context the way per-hover FoilCanvas contexts did.

let _ctx = null;

function _ensureContext() {
  if (_ctx) return _ctx;
  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border-radius:inherit;pointer-events:none;z-index:5';

  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, premultipliedAlpha: false });
  // Raw passthrough (matches FoilCanvas/foilStage): NoColorSpace texture +
  // LinearSRGB output puts the exact image bytes on the canvas so the foil
  // surface matches the non-foil DOM <img> underneath it.
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  renderer.setClearColor(0x000000, 0);
  // Floor 2 (super-sample on DPR-1 Windows) + cap 3 (4K Windows at 200%+ has
  // DPR 2.5-3; capping at 2 left the buffer below the physical screen -> blurry).
  // Single-card canvas, so 3x is cheap. Phones skip the floor AND cap at 1.5:
  // their dpr is already 2-3, the super-sample buys nothing at preview size,
  // and buffer bytes count against iOS Safari's tight per-tab memory budget.
  renderer.setPixelRatio(isPhoneLayout()
    ? Math.min(window.devicePixelRatio || 1, 1.5)
    : Math.min(Math.max(window.devicePixelRatio || 1, 2), 3));

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const uniforms = buildFoilUniforms({ zones: FOIL_WHITE_ZONES });
  getFoilMatcap((tex) => { uniforms.uMatcap.value = tex; });
  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: FOIL_VERTEX_SHADER,
    fragmentShader: FOIL_FRAGMENT_SHADER,
    transparent: true,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  scene.add(mesh);

  const ctx = {
    canvas, renderer, scene, camera, uniforms, mesh, material,
    lost: false, raf: 0, ready: false, last: 0,
    imageUrl: null, owner: null, lastW: 0, lastH: 0,
  };

  canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); ctx.lost = true; }, false);
  canvas.addEventListener('webglcontextrestored', () => {
    ctx.lost = false;
    ctx.dirty = true; // restored context -> re-render to repaint the surface
    [uniforms.uTex, uniforms.uMatcap, uniforms.uFoilMap, uniforms.uDepthMap].forEach((u) => {
      if (u && u.value) u.value.needsUpdate = true;
    });
  }, false);

  _ctx = ctx;
  return ctx;
}

function _resize() {
  const c = _ctx;
  if (!c) return;
  const w = c.canvas.clientWidth;
  const h = c.canvas.clientHeight;
  if (!w || !h || (w === c.lastW && h === c.lastH)) return;
  c.lastW = w;
  c.lastH = h;
  c.renderer.setSize(w, h, false);
  _applyObjectCover();
  c.dirty = true; // size/crop changed -> next frame must render
}

// Object-cover crop (identical to FoilCanvas): crop the card-art texture's
// over-long axis into the quad and center it, so landscape site art fills its
// portrait tile like the DOM object-cover <img>.
function _applyObjectCover() {
  const c = _ctx;
  const img = c?.uniforms?.uTex?.value?.image;
  if (!c || !img || !img.width || !img.height) return;
  // Rotated sites: rotated portrait aspect already matches the landscape quad,
  // so the cover crop is identity (the unrotated crop would be bogus).
  if (c.rotate90) {
    c.uniforms.uUvScale.value.set(1, 1);
    c.uniforms.uUvOffset.value.set(0, 0);
    return;
  }
  const w = c.canvas.clientWidth;
  const h = c.canvas.clientHeight;
  if (!w || !h) return;
  const texAspect = img.width / img.height;
  const quadAspect = w / h;
  let usX = 1, usY = 1, uoX = 0, uoY = 0;
  if (texAspect > quadAspect) {
    usX = quadAspect / texAspect;
    uoX = (1 - usX) / 2;
  } else if (texAspect < quadAspect) {
    usY = texAspect / quadAspect;
    uoY = (1 - usY) / 2;
  }
  c.uniforms.uUvScale.value.set(usX, usY);
  c.uniforms.uUvOffset.value.set(uoX, uoY);
}

function _setCard({ imageUrl, zones, params, rotate90 }) {
  const c = _ensureContext();
  c.rotate90 = !!rotate90;
  c.uniforms.uRotate90.value = rotate90 ? 1 : 0;
  const z = zones || FOIL_WHITE_ZONES;
  const z0 = z[0];
  const z1 = z[1] || z0;
  c.uniforms.uZone0.value.set(z0.x, z0.y, z0.w, z0.h);
  c.uniforms.uZone1.value.set(z1.x, z1.y, z1.w, z1.h);
  const p = params || DEFAULT_FOIL_PARAMS;
  for (const k of Object.keys(DEFAULT_FOIL_PARAMS)) {
    c.uniforms[paramUniformName(k)].value = p[k];
  }
  c.dirty = true; // zones/params/rotate90 (and possibly a new card) changed
  if (c.imageUrl === imageUrl) return; // same card: zones/params updated, keep texture
  c.imageUrl = imageUrl;
  c.ready = false;

  // Drop the previous card's textures. Same context that created them, so this
  // is the safe-disposal case. The matcap + placeholder are shared app-wide and
  // never disposed here.
  const placeholder = foilPlaceholderTexture();
  c.uniforms.uTex.value?.dispose?.();
  c.uniforms.uTex.value = null;
  const prevFoil = c.uniforms.uFoilMap.value;
  if (prevFoil && prevFoil !== placeholder) prevFoil.dispose?.();
  c.uniforms.uFoilMap.value = placeholder;
  c.uniforms.uHasFoilMap.value = 0;
  const prevDepth = c.uniforms.uDepthMap.value;
  if (prevDepth && prevDepth !== placeholder) prevDepth.dispose?.();
  c.uniforms.uDepthMap.value = placeholder;
  c.uniforms.uHasDepthMap.value = 0;
  // Clear once so the previous card's pixels don't linger while the new texture
  // decodes (the loop skips rendering until ready -> the DOM <img> shows).
  if (c.lastW && c.lastH) c.renderer.clear();

  loadFoilTextures(imageUrl, {
    texColorSpace: THREE.NoColorSpace,
    onTex: (tex) => {
      if (c.imageUrl !== imageUrl) { tex.dispose?.(); return; } // superseded by a faster hover
      c.uniforms.uTex.value = tex;
      if (tex.image) c.uniforms.uTexel.value.set(1 / tex.image.width, 1 / tex.image.height);
      c.ready = true;
      c.dirty = true; // card art just became drawable
      _applyObjectCover();
    },
    onFoilMap: (tex) => {
      if (c.imageUrl !== imageUrl) { tex.dispose?.(); return; }
      c.uniforms.uFoilMap.value = tex;
      c.uniforms.uHasFoilMap.value = 1;
      c.dirty = true; // foil map arrived -> shader output changed
    },
    onDepthMap: (tex) => {
      if (c.imageUrl !== imageUrl) { tex.dispose?.(); return; }
      c.uniforms.uDepthMap.value = tex;
      c.uniforms.uHasDepthMap.value = 1;
      c.dirty = true; // depth map arrived -> parallax now uses real depth
    },
  });
}

function _start() {
  const c = _ctx;
  if (!c || c.raf) return;
  c.last = 0;
  c.lastTilt = null;
  const loop = (t) => {
    c.raf = requestAnimationFrame(loop);
    _resize(); // sets c.dirty if the host box changed
    if (!c.ready || c.lost) return;
    const dt = c.last ? (t - c.last) / 1000 : 0;
    c.last = t;
    c.uniforms.uTime.value += dt; // unused by the shader; kept for parity
    const tilt = c.tilt || { mx: 50, my: 50, fromCenter: 0 };
    // Gate redraws: render only when an input that affects the frame changed.
    // uTime is unused by the shader, so an idle frame (same tilt, no dirty) is
    // pixel-identical to the last -> skip renderer.render but keep the rAF alive.
    const tiltChanged =
      !c.lastTilt ||
      tilt.mx !== c.lastTilt.mx ||
      tilt.my !== c.lastTilt.my ||
      (tilt.fromCenter || 0) !== c.lastTilt.fromCenter;
    if (!c.dirty && !tiltChanged) return;
    c.uniforms.uTilt.value.set((tilt.mx / 100) - 0.5, (tilt.my / 100) - 0.5);
    c.uniforms.uFromCenter.value = tilt.fromCenter || 0;
    c.lastTilt = { mx: tilt.mx, my: tilt.my, fromCenter: tilt.fromCenter || 0 };
    c.renderer.render(c.scene, c.camera);
    c.dirty = false;
  };
  c.raf = requestAnimationFrame(loop);
}

function _stop() {
  if (_ctx?.raf) { cancelAnimationFrame(_ctx.raf); _ctx.raf = 0; }
}

// Thin Preact wrapper: reparents the shared canvas into its host on mount, swaps
// the card on prop change, and parks the canvas (context kept alive) on unmount.
export default class FoilPreview extends Component {
  hostRef = createRef();

  componentDidMount() {
    // _ensureContext + _setCard call new THREE.WebGLRenderer / texture loads,
    // which throw at the browser's ~16-live-context ceiling (rapid hover churn).
    // Mirror FoilCanvas/CardFoilScene: swallow the throw and leave the DOM <img>
    // underneath visible (we render nothing) rather than letting it propagate
    // out of the lifecycle. The happy path is unchanged.
    try {
      const c = _ensureContext();
      const host = this.hostRef.current;
      if (!host) return;
      host.appendChild(c.canvas);
      c.owner = this;
      c.tilt = this.props.tilt;
      c.lastW = 0; // force a resize against the new host box
      _setCard(this.props);
      _start();
    } catch (err) {
      console.warn('[FoilPreview] WebGL init failed, falling back to DOM image:', err?.message || err);
      _stop();
    }
  }

  componentDidUpdate() {
    if (_ctx?.owner !== this) return;
    try {
      _ctx.tilt = this.props.tilt;
      _setCard(this.props);
    } catch (err) {
      console.warn('[FoilPreview] WebGL update failed, falling back to DOM image:', err?.message || err);
    }
  }

  componentWillUnmount() {
    const c = _ctx;
    if (c?.owner !== this) return; // a newer preview already took the canvas
    _stop();
    c.owner = null;
    if (c.canvas.parentNode) c.canvas.parentNode.removeChild(c.canvas);
  }

  render() {
    return (
      <div
        ref={this.hostRef}
        style={{ position: 'absolute', inset: 0, borderRadius: 'inherit', pointerEvents: 'none' }}
      />
    );
  }
}
